import { useCallback } from 'react';
import { useUser } from '@/hooks/user/useUser';
import { useChatbotAccess } from '@/hooks/useChatbotAccess';
import { ChatbotIntroModal } from '@/components/ChatbotIntroModal/ChatbotIntroModal';
import { FloatingActionButton } from '@/components/FloatingActionBar/FloatingActionButton';
import { ZigBotAvatar } from '@/components/ZigBot/ZigBotAvatar';
import { ZigBotAttentionGlow } from '@/components/ZigBot/ZigBotAttentionGlow';
import { useZigBotIdleAttention } from '@/components/ZigBot/useZigBotIdleAttention';

/**
 * ZigBot's standalone entry point. Available to every authenticated user regardless of age;
 * renders nothing only when the chatbot flag is off. The intro modal always renders, since
 * unmounting it the moment access is granted would drop the acknowledgement mid-flight.
 */
export const ZigBotFab = () => {
    const { user } = useUser();
    const {
        isVisible,
        onActivate,
        isModalOpen,
        setIsModalOpen,
        grantChatbotAccess,
        onAccessConfirmed,
    } = useChatbotAccess();

    const { isAnimating, dismiss, onAnimationComplete } = useZigBotIdleAttention(user?.id ?? '', isVisible);

    const handleClick = useCallback(() => {
        dismiss();
        onActivate();
    }, [dismiss, onActivate]);

    return (
        <>
            {isVisible && (
                <FloatingActionButton
                    label="Open ZigBot"
                    // Opens the chat sheet, or the intro modal first. Both are dialogs.
                    aria-haspopup="dialog"
                    onClick={handleClick}
                    className="relative"
                    data-track-click="ZigBot Clicked"
                    data-track-location="floating-action-bar"
                    data-track-button-name="zigbot"
                >
                    {isAnimating && <ZigBotAttentionGlow onFinished={onAnimationComplete} />}
                    {/* Positioned so it stacks above the glow, which is painted over the button's
                        own background. */}
                    <span className="relative z-10 size-full">
                        <ZigBotAvatar />
                    </span>
                </FloatingActionButton>
            )}
            <ChatbotIntroModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                confirm={grantChatbotAccess}
                onConfirmed={onAccessConfirmed}
            />
        </>
    );
};
